import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Stats.scss';
import { useTranslation } from 'react-i18next';
import { DataTableExpandedRows, DataTableRowToggleEvent } from 'primereact/datatable';
import { DataTable } from '../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { Button } from 'primereact/button';
import { useFilters } from '../../../../../shared/contexts/filters/FiltersContext';
import useExport from '../../../../../shared/hooks/global/UseExport';
import LineChart from '../../../../../shared/components/chart/line-chart/LineChart';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { TileBox } from '../../../../projects/preview/components/tile-box/TileBox';
import { BrandsTable } from '../../../../projects/preview/components/brand-table/BrandsTable';
import ShareOfVoiceBarChart from '../../../../projects/preview/components/sov-bar-chart/ShareOfVoiceBarChart';
import ChatResponse from './components/chat-response/ChatResponse';
import AnswerAdSlot from './components/feature-panels/AnswerAdSlot';
import AnswerProductsPanel from './components/feature-panels/AnswerProductsPanel';
import FanoutQueries from './components/fanout-queries/FanoutQueries';
import KeySources from './components/key-sources/KeySources';
import MentionAvatars from './components/mention-avatars/MentionAvatars';
import ProductCardsSection from './components/product-cards/ProductCardsSection';
import ModelTooltips from '../../../../../shared/components/model-tooltips/ModelTooltips';
import BrandIcon from '../../../../../shared/components/brand-icon/BrandIcon';
import useExportPromptData from '../../../../../shared/hooks/prompt/preview/UseExportPromptData';
import { getKeywordMap, getMonitorTypeIcon } from './helpers/StatsHelper';
import { prepareHeaderDate } from '../../../../../shared/helpers/filters/prepareHeaderDate';
import { buildBucketLabelFormatters } from '../../../../../shared/helpers/date/bucketDateLabels';
import { sortMonitorTypesByDisplayOrder } from '../../../../../shared/helpers/MonitorTypeHelpers';
import { formatEventsForLineChart } from '../../../../../shared/services/monitor/events/EventNameFormatter';
import { getMonitorTypeByChatOptionsType } from '../../../../../shared/helpers/StatsHelpers';
import { getMonitorData } from '../../../../../shared/api/services/monitor/data/MonitorDataService';
import { KeywordsDailyStatsResponse } from '../../../../../shared/models/monitor/keywords/stats/KeywordsDailyStatsResponse';
import {
  calculatePaginationValues,
  calculateSafeFirst,
  createPaginationHandler,
} from '../../../../../shared/helpers/PaginationHelper';
import { PromptResponse } from '../../../../../shared/models/monitor/PromptResponse';
import { KeywordResponse } from '../../../../../shared/models/monitor/KeywordResponse';
import { TagsResponse } from '../../../../../shared/models/tags/TagsResponse';
import { MonitorEventLogResponse } from '../../../../../shared/models/monitor/MonitorEventLogResponse';
import {
  DayAggregate,
  MonitorDataRecord,
  MonitorDataResponse,
} from '../../../../../shared/models/monitor/MonitorDataResponse';
import {
  AllBrandsStatsResponse,
  ProjectVisibilityBrandPoint,
} from '../../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import { ProjectSovPerModelPoint } from '../../../../../shared/api/services/project/stats/ProjectDailyStatsService';
import { PromptBrandsStatsResponse } from '../../../../../shared/models/monitor/keywords/stats/PromptBrandsStatsResponse';
import {
  ProductItem,
  PromptFanoutResponse,
  PromptFeaturesSummaryItem,
} from '../../../../../shared/models/monitor/ResponseFeatures';
import { SourceDataResponse } from '../../../../../shared/models/monitor/data/sources/SourceDataResponse';
import { findTagIdInKeyword, resolveBrandKey } from '../../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../../shared/helpers/tags/TagsHelper';
import { SOV_PIE_CHART_PALETTE } from '../../../../../shared/utils/sovPalette';
import { SELECTED_BRAND_COLOR, normalizeKeyword } from '../../../../../shared/utils/utils';
import { TAG_NAME } from '../../../../../shared/utils/urls';
import {
  ALL_PROJECT_CHAT_TYPES,
  ChatType,
  getChatTypeToName,
} from '../../../../../shared/types/monitor/ChatType';
import { PeriodType } from '../../../../../shared/types/prompt/PeriodType';
import { useEffectiveAddons } from '../../../../../shared/contexts/effective-features/EffectiveFeaturesContext';
import usePermission from '../../../../../shared/hooks/global/UsePermission';
import useIsMobile from '../../../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../../../shared/utils/mobile';

const TOP_BRANDS_LIMIT = 7;
const SOV_BARS_LIMIT = 4;
const GPT_FAMILY_PREFIX = 'GPT';

interface StatsProps {
  promptData: PromptResponse;
  /** Marka projektu — plakietka „Marka główna” w rankingach. */
  projectDefaultKeyword?: string;
  keywordResponse: KeywordResponse[];
  tags: TagsResponse[];
  monitorEventsData: MonitorEventLogResponse[] | null;
  promptBrands: PromptBrandsStatsResponse | null;
  isBrandsStatsLoading: boolean;
  keywordsDailyStats: KeywordsDailyStatsResponse | null;
  /** Same series, always in DAY buckets — Explore AI Answers does not follow the page period. */
  keywordsDailyStatsPerDay: KeywordsDailyStatsResponse | null;
  fanout: PromptFanoutResponse | null;
  isFanoutLoading: boolean;
  featuresSummary: PromptFeaturesSummaryItem[] | null;
  sourcesData: SourceDataResponse | null;
  latestProducts: ProductItem[] | null;
  allowedMonitorTypes: Set<string> | null;
  filterMonitorTypes?: string[];
  monitorData: MonitorDataResponse | null;
  isMonitorDataLoading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  totalAnswers?: number;
}

interface StatsTableRow {
  created: string;
  averagePosition: string;
  averageShareOfVoice: string;
  aggregate?: DayAggregate;
}

const Stats: React.FC<StatsProps> = ({
  promptData,
  projectDefaultKeyword,
  keywordResponse,
  tags,
  monitorEventsData,
  promptBrands,
  isBrandsStatsLoading,
  keywordsDailyStats,
  keywordsDailyStatsPerDay,
  fanout,
  isFanoutLoading,
  featuresSummary,
  sourcesData,
  latestProducts,
  allowedMonitorTypes,
  filterMonitorTypes,
  monitorData,
  isMonitorDataLoading,
  onPageChange,
  totalAnswers,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  // Tylko do CHOWANIA eksportów (zamkniętych dla gościa na backendzie) — nigdy jako przełącznik
  // ścieżki zapisu: hook odmawia, dopóki konto się nie wczyta.
  const projectsAccess = usePermission('PROJECTS');
  const { monitorPeriod, selectedChatType } = useFilters();
  const { exportStats, exportSources, exportAiAnswers } = useExportPromptData(promptData.id);
  const { canExport } = useExport();
  const [isExportingAnswers, setIsExportingAnswers] = useState(false);
  const [isExportingStats, setIsExportingStats] = useState(false);

  const handleExportAnswers = async () => {
    if (isExportingAnswers || !canExport) return;
    setIsExportingAnswers(true);
    try {
      await exportAiAnswers(promptData.keyword);
    } finally {
      setIsExportingAnswers(false);
    }
  };

  const handleExportStats = async () => {
    if (isExportingStats || !canExport) return;
    setIsExportingStats(true);
    try {
      await exportStats();
    } finally {
      setIsExportingStats(false);
    }
  };

  // Hover a brand row → highlight its line on the Visibility chart (opacity dimming, like the
  // Sources tab). No hover = no selection, so every series renders active (full opacity) on load.
  const [hoveredBrand, setHoveredBrand] = useState<string | null>(null);

  // Visibility Overview metric toggle (session-only, defaults to visibility): the multi-brand
  // visibility % chart, or the tracked brand's Position & Share of Voice on one dual-axis chart.
  const [overviewView, setOverviewView] = useState<'visibility' | 'combined'>('visibility');

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | null>(null);
  const [expandedRowsData, setExpandedRowsData] = useState<Record<string, MonitorDataRecord[]>>({});
  const [errorRows, setErrorRows] = useState<Set<string>>(new Set());
  const expandedRowsDataRef = useRef<Record<string, MonitorDataRecord[]>>({});
  const loadingRowsRef = useRef<Set<string>>(new Set());

  // Expanded-row content is fetched once per bucket and cached. A new monitorData means the filter /
  // period / page changed, so the cached content is stale (e.g. narrowed to fewer models) — drop it
  // and collapse rows, so the next expand refetches with the current scope.
  useEffect(() => {
    expandedRowsDataRef.current = {};
    loadingRowsRef.current = new Set();
    setExpandedRowsData({});
    setErrorRows(new Set());
    setExpandedRows(null);
  }, [monitorData]);

  const keywordsMapper = useMemo(() => getKeywordMap(keywordResponse), [keywordResponse]);
  const originalNamesMapper = useMemo(() => getKeywordMap(keywordResponse, false), [keywordResponse]);

  const monitorChartEvents = useMemo(
    () =>
      formatEventsForLineChart(
        (monitorEventsData ?? []).filter((event) =>
          ['MODEL_ADDED', 'MODEL_DELETED', 'MODEL_RELEASED'].includes(event.type)
        )
      ),
    [monitorEventsData]
  );

  const resolveName = useCallback(
    (keyword: string, mapper: Map<string, string>): string => {
      // Brand keys come tag-resolved (chatbeat_tag_name_<id>) for tag-managed brands — map straight
      // to the tag label; otherwise resolve a raw keyword to its tag/display name.
      if (keyword.startsWith(TAG_NAME)) {
        const tagId = Number(keyword.slice(TAG_NAME.length));
        return getTagLabel(tags ?? [], tagId, keyword).replace(/\b\w/g, (c) => c.toUpperCase());
      }
      const tagId = keywordResponse ? findTagIdInKeyword(keyword, keywordResponse) : null;
      const keywordFromTags = tags ? getTagLabel(tags, tagId, keyword) : keyword;
      const raw = mapper.get(keywordFromTags) || keywordFromTags;
      return raw.replace(/\b\w/g, (c) => c.toUpperCase());
    },
    [keywordResponse, tags]
  );
  const resolveLabel = useCallback(
    (keyword: string) => resolveName(keyword, keywordsMapper),
    [resolveName, keywordsMapper]
  );
  // Explore AI Answers zostaje przy nazwach z odpowiedzi — bez etykiet marek.
  const resolveOriginalName = useCallback(
    (keyword: string) => resolveName(keyword, originalNamesMapper),
    [resolveName, originalNamesMapper]
  );

  const firmName = useCallback((): string => {
    return resolveLabel(promptData.keyword);
  }, [resolveLabel, promptData.keyword]);

  // Per-brand ranking rows built LIVE from this prompt's monitor_keyword_stats + monitor_data:
  // visibility = mentioned/total (raw, matches the Explore AI Answers table), Share of Voice comes
  // normalised from the backend (sums to 100 across every brand of the prompt — see
  // KeywordsStatsService.normaliseSov). Shaped as AllBrandsStatsResponse so the tables/charts consume it.
  // Owner's addons when a superadmin views a client's prompt — the same source the backend gates on.
  const hasNewStats = useEffectiveAddons().includes('NEW_STATS');

  const scoredBrands = useMemo<AllBrandsStatsResponse[]>(() => {
    const total = promptBrands?.totalAnswers ?? 0;
    return (promptBrands?.brands ?? []).map((b) => ({
      keyword: b.keyword,
      brandScore: 0,
      position: b.position ?? 0,
      medianPosition: b.position ?? 0,
      shareOfVoice: b.shareOfVoice,
      visibility: total > 0 ? Math.round((b.mentioned / total) * 1000) / 10 : 0,
      visibilityTrend: 0,
      trends: { positionTrend: 0, visibilityTrend: 0, medianTrend: 0 },
    }));
  }, [promptBrands]);

  const allBrandsStats = scoredBrands;

  // Per-brand-per-date visibility for the Visibility Overview chart: mentioned/total per bucket (raw).
  const visibilityChartPerBrand = useMemo<ProjectVisibilityBrandPoint[]>(() => {
    const totalByDate = promptBrands?.totalByDate ?? {};
    return (promptBrands?.perDate ?? []).map((p) => ({
      date: p.date,
      brand: p.keyword,
      visibility: totalByDate[p.date] > 0 ? Math.round((p.mentioned / totalByDate[p.date]) * 1000) / 10 : 0,
    }));
  }, [promptBrands]);

  // Per-brand-per-model Share of Voice — normalised WITHIN each model by the backend, so one model's
  // bars sum to 100%.
  const sovPerModel = useMemo<ProjectSovPerModelPoint[]>(
    () =>
      (promptBrands?.perModel ?? []).map((p) => ({
        chatType: p.chatType,
        keyword: p.keyword,
        shareOfVoice: p.shareOfVoice,
        position: p.position ?? undefined,
      })),
    [promptBrands]
  );

  // Ordering for the Top brands table, chart series and colors. Without NEW_STATS the Share of Voice
  // comes from the pre-2026-06-20 formula, which only covers the brands the old daily series returned
  // (top-10 by position) — anything else lands on 0. Ordering by visibility then pulled those zeros
  // straight into the top 7, so on that path we order by Share of Voice instead.
  const sortedBrands = useMemo(
    () =>
      [...scoredBrands].sort((a, b) =>
        hasNewStats
          ? (b.visibility ?? 0) - (a.visibility ?? 0)
          : (b.shareOfVoice ?? 0) - (a.shareOfVoice ?? 0)
      ),
    [scoredBrands, hasNewStats]
  );

  // Rank of every brand within the full sorted list (1-based). The side table's "#"
  // column reads this so the tracked brand keeps its real position (e.g. 15) even when it is
  // appended below the top 7 instead of sitting at its natural row index.
  const brandRankByKeyword = useMemo(
    () =>
      new Map(sortedBrands.map((b, i) => [normalizeKeyword(b.keyword).toLowerCase(), i + 1])),
    [sortedBrands]
  );

  // Always TOP_BRANDS_LIMIT brands including the tracked one — drives the chart + side table.
  // When the tracked brand ranks below 7th we drop the lowest-visibility competitor to keep it in
  // (never a duplicate: this branch runs only when it's absent from the natural top 7). Its "#"
  // still shows its real rank via brandRankByKeyword. The row already lives in allBrandsStats
  // (the backend force-includes the firmName row); we just stop slicing it out here.
  const topBrands = useMemo(() => {
    const top = sortedBrands.slice(0, TOP_BRANDS_LIMIT);
    const target = normalizeKeyword(promptData.keyword).toLowerCase();
    if (top.some((b) => normalizeKeyword(b.keyword).toLowerCase() === target)) {
      return top;
    }
    const ownBrand = sortedBrands.find((b) => normalizeKeyword(b.keyword).toLowerCase() === target);
    return ownBrand ? [...top.slice(0, TOP_BRANDS_LIMIT - 1), ownBrand] : top;
  }, [sortedBrands, promptData.keyword]);

  const brandColors = useMemo(() => {
    const target = normalizeKeyword(promptData.keyword).toLowerCase();
    return topBrands.map((brand, index) => ({
      name: resolveLabel(brand.keyword),
      color:
        normalizeKeyword(brand.keyword).toLowerCase() === target
          ? SELECTED_BRAND_COLOR
          : SOV_PIE_CHART_PALETTE[index % SOV_PIE_CHART_PALETTE.length],
    }));
  }, [topBrands, resolveLabel, promptData.keyword]);

  // Etykiety osi/tooltipa jak na wykresach projektowych ("Jul 21", zakres tygodnia w tooltipie).
  const dateLabelFormatters = useMemo(
    () => buildBucketLabelFormatters(monitorPeriod, i18n.language),
    [monitorPeriod, i18n.language]
  );

  const brandColorByOriginalName = useMemo(
    () => new Map(topBrands.map((brand, index) => [resolveOriginalName(brand.keyword), brandColors[index].color])),
    [topBrands, brandColors, resolveOriginalName]
  );

  // Pivot {date, brand, visibility}[] → [['date', brandA…], [bucket, %…]] for the top brands.
  const visibilityMatrix = useMemo(() => {
    const brandLabels = topBrands.map((b) => resolveLabel(b.keyword));
    const header: (string | number)[] = ['date', ...brandLabels];
    if (visibilityChartPerBrand.length === 0 || brandLabels.length === 0) {
      return [header];
    }

    const byDate = new Map<string, Map<string, number>>();
    for (const point of visibilityChartPerBrand) {
      if (!byDate.has(point.date)) {
        byDate.set(point.date, new Map());
      }
      byDate.get(point.date)!.set(resolveLabel(point.brand), point.visibility);
    }

    const rows = Array.from(byDate.keys())
      .sort()
      .map((date) => [
        prepareHeaderDate(date, (monitorPeriod ?? 'DAILY') as PeriodType),
        ...brandLabels.map((label) => byDate.get(date)?.get(label) ?? 0),
      ]);

    return [header, ...rows];
  }, [visibilityChartPerBrand, topBrands, resolveLabel, monitorPeriod]);

  // The keywords-daily-stats response keys the tracked brand by its TAG key (chatbeat_tag_name_<id>)
  // when it belongs to a tag, else by the raw keyword — mirror that so the lookup hits.
  const trackedBrandKey = useMemo(() => {
    if (promptData.keyword.startsWith(TAG_NAME)) return promptData.keyword;
    const tagId = findTagIdInKeyword(promptData.keyword, keywordResponse);
    return tagId != null ? `${TAG_NAME}${tagId}` : promptData.keyword;
  }, [promptData.keyword, keywordResponse]);

  // Tracked brand's average position over time — single line for the "Brand Positioning Overview"
  // chart. Sourced LIVE from monitor_keyword_stats (per-answer positions), the same basis as the
  // Explore AI Answers table, so the chart and the table agree.
  const positionMatrix = useMemo(() => {
    const records = keywordsDailyStats?.keywordsDailyStatsData?.[trackedBrandKey] ?? [];
    if (records.length === 0) return [];
    const sorted = [...records].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    return [
      ['date', t('averagePosition')],
      ...sorted.map((r) => [
        prepareHeaderDate(String(r.date), (monitorPeriod ?? 'DAILY') as PeriodType),
        r.averagePosition ?? null,
      ]),
    ] as (string | number)[][];
  }, [keywordsDailyStats, trackedBrandKey, t, monitorPeriod]);



  // ['model', ...brands] + one row per model with data — feeds the grouped-bar SoV chart.
  // Top SOV_BARS_LIMIT brands by SoV; the tracked brand is always force-included (swapping out the
  // lowest-SoV brand when it's outside the top) so its bar is never missing.
  const sovByModelMatrix = useMemo(() => {
    const allLabels = topBrands.map((b) => resolveLabel(b.keyword));

    // Already normalised per model in `sovPerModel` — do NOT re-derive from position here, that was
    // the raw weight curve and made the bars of one model sum well past 100%.
    const sovForPoint = (p: ProjectSovPerModelPoint) => p.shareOfVoice;

    const labelsBySov = [...allLabels]
      .map((label) => {
        const totalSov = sovPerModel
          .filter((p) => resolveLabel(p.keyword) === label)
          .reduce((sum, p) => sum + sovForPoint(p), 0);
        return { label, totalSov };
      })
      .sort((a, b) => b.totalSov - a.totalSov);

    const topSovLabels = labelsBySov.slice(0, SOV_BARS_LIMIT).map((e) => e.label);
    // Always show the tracked brand: if it falls outside the top SoV, swap out the lowest-SoV brand
    // for it (keeps SOV_BARS_LIMIT bars). It's highlighted via brandColors (SELECTED_BRAND_COLOR).
    const trackedLabel = resolveLabel(promptData.keyword);
    if (allLabels.includes(trackedLabel) && !topSovLabels.includes(trackedLabel)) {
      topSovLabels[topSovLabels.length - 1] = trackedLabel;
    }
    const sovLabelsSet = new Set(topSovLabels);

    const brandLabels = allLabels.filter((l) => sovLabelsSet.has(l));
    if (sovPerModel.length === 0 || brandLabels.length === 0) {
      return null;
    }

    const byChatType = new Map<string, Map<string, number>>();
    for (const point of sovPerModel) {
      if (!byChatType.has(point.chatType)) {
        byChatType.set(point.chatType, new Map());
      }
      byChatType.get(point.chatType)!.set(resolveLabel(point.keyword), sovForPoint(point));
    }

    const rows = ALL_PROJECT_CHAT_TYPES.filter((chatType) => byChatType.has(chatType)).map(
      (chatType) => [
        getChatTypeToName(chatType as ChatType),
        ...brandLabels.map((label) => String(byChatType.get(chatType)?.get(label) ?? 0)),
      ]
    );

    if (rows.length === 0) {
      return null;
    }

    return [['model', ...brandLabels], ...rows] as string[][];
  }, [sovPerModel, topBrands, resolveLabel, promptData.keyword]);

  // Tiles: the tracked brand's row from the brands stats (single consistent source).
  const targetBrandStats = useMemo(() => {
    const target = normalizeKeyword(promptData.keyword).toLowerCase();
    return scoredBrands.find((b) => normalizeKeyword(b.keyword).toLowerCase() === target) ?? null;
  }, [scoredBrands, promptData.keyword]);

  const visibilityTile = targetBrandStats?.visibility ?? null;

  // Period ads/shopping % for the expanded-answer badges (measured-denominator semantics).
  const { adsPeriodPercent, shoppingPeriodPercent } = useMemo(() => {
    if (!featuresSummary || featuresSummary.length === 0) {
      return { adsPeriodPercent: null, shoppingPeriodPercent: null };
    }
    const gptRows = featuresSummary.filter((row) => row.type.startsWith(GPT_FAMILY_PREFIX));
    const adsMeasured = gptRows.reduce((sum, row) => sum + row.adsMeasured, 0);
    const adsAnswers = gptRows.reduce((sum, row) => sum + row.adsAnswers, 0);
    const shoppingMeasured = featuresSummary.reduce((sum, row) => sum + row.shoppingMeasured, 0);
    const shoppingAnswers = featuresSummary.reduce((sum, row) => sum + row.shoppingAnswers, 0);
    return {
      adsPeriodPercent: adsMeasured > 0 ? Math.round((adsAnswers / adsMeasured) * 100) : null,
      shoppingPeriodPercent:
        shoppingMeasured > 0 ? Math.round((shoppingAnswers / shoppingMeasured) * 100) : null,
    };
  }, [featuresSummary]);

  // Share of Voice per bucket, taken from the daily series where the backend already normalised it
  // over every brand of the prompt. Averaging the per-answer `shareOfVoice` here instead served the
  // raw SCORE_WEIGHT of the position under a `%` sign — 27.3 for position 4, where the real share was
  // 20.3. Bucket keys line up: MonitorDataLocalService.getDateBucket mirrors getFormatDate.
  const sovByBucket = useMemo(
    () =>
      new Map(
        (keywordsDailyStatsPerDay?.keywordsDailyStatsData?.[trackedBrandKey] ?? []).map((record) => [
          String(record.date),
          record.averageShareOfVoice,
        ])
      ),
    [keywordsDailyStatsPerDay, trackedBrandKey]
  );

  // Explore AI Answers rows: one per date bucket, aggregates straight from the backend.
  const tableData: StatsTableRow[] = useMemo(() => {
    if (!monitorData) return [];

    return Object.entries(monitorData.data)
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([bucket, records]) => {
        // Average Position = mean of the tracked brand's position over the answers RETURNED for this
        // day (every model in scope where the brand was mentioned) — matches exactly the rows shown
        // when the bucket is expanded. Share of Voice does NOT: it is normalised across the prompt's
        // brands, so it cannot be re-derived from the expanded rows alone.
        const positions = records.map((r) => r.position).filter((p) => p > 0);
        const avgPos =
          positions.length > 0
            ? (positions.reduce((a, b) => a + b, 0) / positions.length).toFixed(1)
            : '-';
        const sov = sovByBucket.get(bucket);
        const avgSov = typeof sov === 'number' && sov > 0 ? `${sov.toFixed(1)}%` : '-';
        return {
          created: bucket,
          averagePosition: avgPos,
          averageShareOfVoice: avgSov,
          aggregate: monitorData.dayAggregates?.[bucket],
        };
      });
  }, [monitorData, sovByBucket]);

  const handlePageChange = createPaginationHandler(monitorData?.pagination, onPageChange);
  const { first, rows, totalRecords } = calculatePaginationValues(monitorData?.pagination, tableData.length);
  const safeFirst = calculateSafeFirst(first, rows, totalRecords);

  const loadRowContent = useCallback(
    async (dateKey: string) => {
      if (expandedRowsDataRef.current[dateKey] || loadingRowsRef.current.has(dateKey)) {
        return;
      }
      loadingRowsRef.current.add(dateKey);

      try {
        const chatType = selectedChatType ? getMonitorTypeByChatOptionsType(selectedChatType) : 'ALL';
        const response = await getMonitorData(
          promptData.id,
          promptData.keyword,
          'DAILY',
          chatType,
          dateKey,
          dateKey,
          1,
          100,
          true,
          undefined,
          filterMonitorTypes
        );
        const records = response.data[dateKey] || [];
        expandedRowsDataRef.current = { ...expandedRowsDataRef.current, [dateKey]: records };
        setExpandedRowsData((prev) => ({ ...prev, [dateKey]: records }));
      } catch (error) {
        console.error('Failed to load row content:', error);
        setErrorRows((prev) => new Set(prev).add(dateKey));
      } finally {
        loadingRowsRef.current.delete(dateKey);
      }
    },
    [promptData, selectedChatType, filterMonitorTypes]
  );

  const handleRowToggle = useCallback(
    (e: DataTableRowToggleEvent) => {
      setExpandedRows(e.data as DataTableExpandedRows);
      if (e.data && !Array.isArray(e.data)) {
        Object.keys(e.data).forEach((dateKey) => {
          if ((e.data as DataTableExpandedRows)[dateKey] && !expandedRowsDataRef.current[dateKey]) {
            loadRowContent(dateKey);
          }
        });
      }
    },
    [loadRowContent]
  );

  const rowExpansionTemplate = (row: StatsTableRow) => {
    const dateKey = row.created;
    const records = expandedRowsData[dateKey];

    if (errorRows.has(dateKey)) {
      return (
        <div className="table-message table-message--loading">
          <span>{t('errorLoadingDataRetry')}</span>
        </div>
      );
    }

    if (!records) {
      return (
        <div className="table-message table-message--loading">
          <Skeleton height="120px"><span /></Skeleton>
        </div>
      );
    }

    if (records.length === 0) {
      return <EmptyPanel />;
    }

    // Show every answer the backend returned for this bucket (already scoped by the active filter /
    // NEW_STATS) — no client-side model re-filter, matching the models-column icons and Key Sources.
    const sortedRecords = [...records].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );

    return (
      <div className="table-message">
        {sortedRecords.map((record, index) => (
          <div key={`chat-${dateKey}-${index}`} id={`chat-${record.id}`}>
            <ChatResponse
              prompt={promptData.prompt}
              currentPosition={record.position}
              created={record.created}
              content={record.content || ''}
              organicContent={record.organicResponse || ''}
              htmlContent={record.htmlResponse || ''}
              monitorType={record.type}
              keyword={promptData.keyword}
              brandNames={monitorData?.brandNames}
              positions={record.positionJson || []}
              stripInlineAds
              // Ad goes in the right column under the brand positions. Only pass the slot when the
              // answer actually has an ad (adsCount is synchronous) — passing it unconditionally
              // would keep the 2-column layout with an empty right column.
              adSlot={
                (record.responseMetadata?.adsCount ?? 0) > 0 ? (
                  <AnswerAdSlot
                    monitorDataId={record.id}
                    responseMetadata={record.responseMetadata}
                    adsPeriodPercent={adsPeriodPercent}
                  />
                ) : undefined
              }
              // Shopping cards live in the right column (below positions/ads), the full list
              // stacked vertically (no inner scroll) — gate on productsCount like the ad slot.
              productsSlot={
                (record.responseMetadata?.productsCount ?? 0) > 0 ? (
                  <AnswerProductsPanel
                    monitorDataId={record.id}
                    responseMetadata={record.responseMetadata}
                    shoppingPeriodPercent={shoppingPeriodPercent}
                  />
                ) : undefined
              }
            />
          </div>
        ))}
      </div>
    );
  };

  const modelsColumnBody = (row: StatsTableRow) => {
    // Icons come straight from the answers the backend returned for this bucket — same principle as
    // Key Sources (which renders each source's models list). The backend already scopes the data by
    // the active filter / NEW_STATS (all models on the "all" view, only the selected ones otherwise),
    // so a client-side re-filter here would only wrongly drop raw md.types not in the expanded set.
    const records = monitorData?.data?.[row.created] ?? [];
    const types = sortMonitorTypesByDisplayOrder(Array.from(new Set(records.map((r) => r.type))));
    // Dedupe by resolved icon (GPT + GPT mini share ico--gpt-bck); names are shown by the shared
    // <ModelTooltips/> mounted once in this component (hover the icon).
    const iconClasses = Array.from(
      new Set(types.map((type) => getMonitorTypeIcon(type)).filter((c): c is NonNullable<typeof c> => !!c))
    );
    return (
      <div className="table__cell-date-icons tags">
        {iconClasses.map((iconClass) => (
          <span key={iconClass} className={`${iconClass} ico--md`} />
        ))}
      </div>
    );
  };

  const handleRowMouseEnter = useCallback((brandLabel: string) => {
    // Match the chart series labels, which are capitalized via `resolveLabel`; the table passes the
    // raw resolved (non-capitalized) label, and the chart's series matching is case-sensitive.
    setHoveredBrand(brandLabel.replace(/\b\w/g, (c) => c.toUpperCase()));
  }, []);

  const handleRowMouseLeave = useCallback(() => {
    setHoveredBrand(null);
  }, []);

  const renderBrandLegendIcon = useCallback(
    (label: string, color?: string) => <BrandIcon name={label} color={color} size="xs" />,
    []
  );

  const isOverviewLoading = isBrandsStatsLoading;

  // Ten sam warunek, przy którym `renderOverviewChart` pokazuje pusty stan.
  const isOverviewEmpty =
    !isOverviewLoading && (overviewView === 'combined' ? positionMatrix : visibilityMatrix).length <= 1;

  const renderOverviewChart = () => {
    if (overviewView === 'combined') {
      // Old "Brand Positioning Overview" chart: the tracked brand's average position over time as a
      // single line (green, Y inverted so lower = better, outside-top-30 handling).
      return positionMatrix.length <= 1 ? (
        <EmptyPanel />
      ) : (
        <LineChart
          firstData={positionMatrix}
          firstColorPalette={[SELECTED_BRAND_COLOR]}
          headers={positionMatrix[0]?.slice(1).map(String) ?? []}
          legendType="FLEX_START"
          inverseY
          showOutside30
          marksData={monitorChartEvents}
          keywordsMapper={keywordsMapper}
          tags={tags}
          commonKeywords={keywordResponse}
          dateAxisFormatter={dateLabelFormatters.axis}
          dateTooltipFormatter={dateLabelFormatters.tooltip}
          chartSizeStyle={{ width: '100%', height: '340px' }}
        />
      );
    }

    return visibilityMatrix.length <= 1 ? (
      <EmptyPanel />
    ) : (
      <LineChart
        firstData={visibilityMatrix}
        headers={visibilityMatrix[0]?.slice(1).map(String) ?? []}
        colorsByName={brandColors}
        selectedBrand={hoveredBrand ?? undefined}
        legendType="FLEX_START"
        inverseY={false}
        autoFitYAxis={false}
        minValue={0}
        maxValue={100}
        formatYAsPercentage
        marksData={monitorChartEvents}
        keywordsMapper={keywordsMapper}
        tags={tags}
        commonKeywords={keywordResponse}
        dateAxisFormatter={dateLabelFormatters.axis}
        dateTooltipFormatter={dateLabelFormatters.tooltip}
        chartSizeStyle={{ width: '100%', height: '340px' }}
        renderLegendIcon={renderBrandLegendIcon}
      />
    );
  };

  return (
    <div className="prompt-stats">
      {/* One set of class-targeted tooltips for every model icon on the page (answers table +
          fanout list). Keyed on the visible data so PrimeReact re-binds after pagination / fanout load. */}
      <ModelTooltips
        key={`mt-${tableData.map((r) => r.created).join('_')}-${fanout?.items.length ?? 0}`}
      />
      <div className="tiles tiles--stats tiles--page">
        <TileBox
          hasData={!isBrandsStatsLoading}
          value={visibilityTile !== null ? Math.round(visibilityTile) : '-'}
          trendValue={targetBrandStats?.visibilityTrend ?? 0}
          label="visibility"
          tooltipText="visibilityTooltip"
          suffix={visibilityTile !== null ? '%' : ''}
        />
        <TileBox
          hasData={!isBrandsStatsLoading}
          value={targetBrandStats?.position ? Number(targetBrandStats.position).toFixed(1) : '-'}
          trendValue={targetBrandStats?.trends?.positionTrend ?? 0}
          label="averagePosition"
          tooltipText="averagePositionTooltip"
        />
        <TileBox
          hasData={!isBrandsStatsLoading}
          value={targetBrandStats ? Number(targetBrandStats.shareOfVoice).toFixed(1) : '-'}
          trendValue={0}
          label="averageShareOfVoice"
          tooltipText="shareOfVoiceTooltip"
          suffix={targetBrandStats ? '%' : ''}
        />
      </div>

      <div className="stats stats--grid">
        <div className="stats__panel">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {overviewView === 'combined' ? t('brandPositioningOverview') : t('visibilityOverview')}
              <CustomTooltip
                tooltipText={
                  overviewView === 'combined' ? 'averagePositionTooltip' : 'visibilityOverviewTooltip'
                }
                addMargin={false}
              />
            </div>
            <div className="prompt-stats__overview-actions">
              {/* Przy pustym wykresie nie ma czego eksportować. */}
              {projectsAccess.write && !isOverviewEmpty && (
              <>
              <Button
                id="btn-export-prompt-stats"
                className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
                onClick={handleExportStats}
              >
                {isExportingStats ? (
                  <span className="ico--loader ico--xs" />
                ) : (
                  <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
                )}
              </Button>
              <CustomTooltip
                tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadStats'}
                targetRef="btn-export-prompt-stats"
              />
              </>
              )}
              {!isOverviewLoading && (
                <div className="chart-view-toggle" role="group" aria-label="Chart metric">
                  <button
                    id="overview-view-visibility"
                    type="button"
                    className={`chart-view-toggle__btn${
                      overviewView === 'visibility' ? ' chart-view-toggle__btn--active' : ''
                    }`}
                    onClick={() => setOverviewView('visibility')}
                    aria-label={t('visibilityOverview')}
                  >
                    <span className="ico--activity ico--xs" />
                  </button>
                  {/* Telefon bez dymków — wystawały poza ekran i przesuwały dolny pasek. */}
                  {!isMobile && (
                    <CustomTooltip tooltipText="visibilityOverview" targetRef="overview-view-visibility" />
                  )}
                  <button
                    id="overview-view-combined"
                    type="button"
                    className={`chart-view-toggle__btn${
                      overviewView === 'combined' ? ' chart-view-toggle__btn--active' : ''
                    }`}
                    onClick={() => setOverviewView('combined')}
                    aria-label={t('brandPositioningOverview')}
                  >
                    <span className="ico--stats ico--xs" />
                  </button>
                  {!isMobile && (
                    <CustomTooltip
                      tooltipText="brandPositioningOverview"
                      targetRef="overview-view-combined"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="stats-box">
            <div className="stats-box__content">
              {isOverviewLoading ? (
                <Skeleton className="skeleton--chart skeleton--chart--tall">
                  <span />
                </Skeleton>
              ) : (
                renderOverviewChart()
              )}
            </div>
          </div>
        </div>

        <div className="stats__side">
          <BrandsTable
            allBrandsStatsResponse={topBrands}
            keywordsMapper={keywordsMapper}
            commonKeywords={keywordResponse}
            tags={tags}
            showVisibility
            showAveragePosition
            titleKey="top7Brands"
            titleTooltipKey="topBrandsTooltip"
            showExport={false}
            paginator={false}
            rows={TOP_BRANDS_LIMIT}
            sortField={hasNewStats ? 'visibility' : 'shareOfVoice'}
            compact
            brandColors={brandColors}
            rankByKeyword={brandRankByKeyword}
            onRowMouseEnter={handleRowMouseEnter}
            onRowMouseLeave={handleRowMouseLeave}
            isLoading={isBrandsStatsLoading}
          />
        </div>
      </div>

      {/* Share of Voice by AI Chatbots — hidden entirely while stats load (no skeleton), like the
          project page; shows the chart when there's data, else the empty panel. */}
      {!isBrandsStatsLoading &&
        (sovByModelMatrix ? (
          <ShareOfVoiceBarChart
            allModelsStatsMatrix={sovByModelMatrix}
            brandColors={brandColors}
            firmName={firmName()}
            keywordsMapper={keywordsMapper}
            commonKeywords={keywordResponse}
            tags={tags}
            renderLegendIcon={renderBrandLegendIcon}
          />
        ) : (
          allBrandsStats.length > 0 && (
            <div className="stats-box stats-box--empty">
              <EmptyPanel />
            </div>
          )
        ))}

      <FanoutQueries fanout={fanout} isLoading={isFanoutLoading} prompt={promptData.prompt} />

      <KeySources
        sourcesData={sourcesData}
        allowedMonitorTypes={allowedMonitorTypes}
        exportSources={exportSources}
        isDemo={!!promptData.demoVersion}
      />

      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('exploreAIAnswers')}
          {totalAnswers != null && totalAnswers > 0 && (
            <span className="prompt-stats__answers-count">
              {t('responsesThisPeriod', { count: totalAnswers })}
            </span>
          )}
        </div>
        {/* Przy pustej tabeli nie ma czego eksportować. */}
        {projectsAccess.write && totalRecords > 0 && (
        <>
          <Button
            id="btn-export-ai-answers"
            className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
            onClick={handleExportAnswers}
          >
            {isExportingAnswers ? (
              <span className="ico--loader ico--xs" />
            ) : (
              <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
            )}
          </Button>
          <CustomTooltip
            tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadAiAnswers'}
            targetRef="btn-export-ai-answers"
          />
        </>
        )}
      </div>
      <DataTable
          stripedRows
          paginator
          lazy
          paginatorClassName="pagination"
          removableSort
          sortField="created"
          sortOrder={-1}
          rows={rows}
          first={safeFirst}
          onPage={handlePageChange}
          totalRecords={totalRecords}
          rowsPerPageOptions={[10, 25, 50]}
          value={isMonitorDataLoading ? [] : tableData}
          tableStyle={{ minWidth: '50rem' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--accordion table-wrapper--stack"
          responsiveLayout="stack"
          expandedRows={expandedRows ?? undefined}
          emptyMessage={() =>
            isMonitorDataLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '240px',
                }}
              >
                <span className="loader__spinner" />
              </div>
            ) : (
              <EmptyPanel />
            )
          }
          onRowToggle={handleRowToggle}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="created"
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate={
            isMobile
              ? MOBILE_PAGINATOR_TEMPLATE
              : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          }
          pageLinkSize={isMobile ? 3 : 5}
        >
          <Column
            sortable
            field="created"
            header={t('date')}
            headerClassName="table__header"
            bodyClassName="table__cell--stack-title"
            style={{ width: '90px' }}
          />
          <Column
            header=""
            headerClassName="table__header"
            bodyClassName="table__cell--stack-wide"
            style={{ minWidth: '180px' }}
            body={modelsColumnBody}
          />
          <Column
            field="averagePosition"
            header={t('averagePosition')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '110px' }}
          />
          <Column
            header={t('visibility')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '90px' }}
            body={(row: StatsTableRow) =>
              row.aggregate ? `${Math.round(row.aggregate.visibility)}%` : '-'
            }
          />
          <Column
            header={t('mentions')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '130px' }}
            body={(row: StatsTableRow) => (
              <MentionAvatars
                brands={(row.aggregate?.mentionedBrands ?? []).map(resolveOriginalName)}
                colorByName={brandColorByOriginalName}
                tooltipId={row.created}
              />
            )}
          />
          <Column
            field="averageShareOfVoice"
            header={t('shareOfVoice')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '110px' }}
          />
          <Column
            header={t('chatgptAds')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '110px' }}
            body={(row: StatsTableRow) => {
              if (!row.aggregate?.hasAds) return '-';
              const iconId = `ads-indicator-${row.created}`;
              return (
                <>
                  <span id={iconId} className="ico--ads ico--sm" />
                  <CustomTooltip tooltipText="chatgptAdsRowTooltip" targetRef={iconId} />
                </>
              );
            }}
          />
          <Column
            header={t('shoppingCards')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '120px' }}
            body={(row: StatsTableRow) => {
              if (!row.aggregate?.hasShoppingCards) return '-';
              const iconId = `shopping-indicator-${row.created}`;
              return (
                <>
                  <span id={iconId} className="ico--shopping-cart ico--sm" />
                  <CustomTooltip tooltipText="shoppingCardsRowTooltip" targetRef={iconId} />
                </>
              );
            }}
          />
          <Column expander bodyClassName="table__cell--stack-actions" style={{ width: '25px' }} />
        </DataTable>

      <ProductCardsSection products={latestProducts} />

      {!isBrandsStatsLoading && scoredBrands.length > TOP_BRANDS_LIMIT && (
        <div className="prompt-stats__all-brands">
          <BrandsTable
            allBrandsStatsResponse={scoredBrands}
            keywordsMapper={keywordsMapper}
            commonKeywords={keywordResponse}
            tags={tags}
            showVisibility
            showAveragePosition
            hideBrandScore
            titleKey="allBrandsRanking"
            titleTooltipKey="allBrandsRankingTooltip"
            sortField={hasNewStats ? 'visibility' : 'shareOfVoice'}
            mainBrandKey={resolveBrandKey(projectDefaultKeyword ?? '', tags)}
            selectedBrandKey={resolveBrandKey(promptData.keyword, tags)}
          />
        </div>
      )}
    </div>
  );
};

export default Stats;
